import network from 'lib/utils/network';
import benefitNetwork from 'lib/utils/network/benefit';
import channelService from 'lib/services/channelService';
import { QueryAMSRoleData } from 'lib/types/AMS';
import { BaseRequestParamsType } from 'lib/types/request';
import { urlConfig } from 'lib/config';
import { getStatisticParams } from 'lib/utils/util';
import { currentUrlParams } from 'lib/utils/url';
import { useMobileStore } from 'lib/store/mobileStore';
import { gateway } from 'lib/utils/authMobile/gatewayMobile';
import qimeiWebDefend from 'lib/utils/defend';
import { GiftDataType, ReceiveGiftRequestData } from './model/gift';
import { OrderPackageRequestData, OrderRequestData } from './model/order';
import { API } from './constants';

function getMobileTokenHeader() {
  const { useGatewayMobile } = useMobileStore.getState();
  const { mobileToken } = gateway;
  return useGatewayMobile && mobileToken ? { mobileToken } : {};
}

export function fetchUserOrderStatus(data) {
  return network.request({
    url: API.USER_ORDER,
    method: 'POST',
    data,
  });
}

export function fetchMobileOrderStatus({ mobile }) {
  return network.request({
    url: API.QUERY_USER_ORDER_BY_MOBILE,
    method: 'POST',
    data: {
      mobile,
    },
    isGateway: true,
    headers: {
      ...getMobileTokenHeader(),
    },
  });
}

export function fetchMobileAvailableOrderPackageList({ mobile, channelCode }) {
  return network.request({
    url: API.PKG_LIST,
    method: 'POST',
    data: {
      mobile,
      channelCode,
    },
    isGateway: true,
    headers: {
      ...getMobileTokenHeader(),
    },
  });
}

export function fetchOrderPackage({
  packageId,
  channelCode,
  mobile,
  subStatTag,
  statisticParams = getStatisticParams(),
  redirectURL,
  shareToken,
}: OrderPackageRequestData) {
  return network.request({
    url: API.PLACE_ORDER,
    method: 'POST',
    data: {
      pId: packageId,
      subStatTag,
      statisticParams,
      mobile,
      channelCode,
      redirectURL,
      inviterToken: shareToken,
      promoterOrderId: currentUrlParams.getCurrentParams('promoterOrderId'),
    },
    isGateway: true,
    headers: {
      ...getMobileTokenHeader(),
    },
  });
}

export function fetchGameCardPlaceOrder() {
  return benefitNetwork.request({
    url: API.GAME_CARD_PLACE_ORDER,
    method: 'POST',
    data: {
      bizType: 3,
      version: '3.0.0',
    },
  });
}

export function fetchSubmitOrder({ orderId, smsCode, mobile }: OrderRequestData) {
  return network.request({
    url: API.SUBMIT_ORDER,
    method: 'POST',
    data: {
      orderId,
      smsCode,
      mobile,
    },
    isGateway: true,
    headers: {
      ...getMobileTokenHeader(),
    },
  });
}

export function fetchDeliverSMSCode({
  packageId,
  channelCode,
  mobile,
  subStatTag,
  statisticParams = getStatisticParams(),
  shareToken,
  promoterOrderId,
}: OrderPackageRequestData) {
  const pageUrl = window.location.href.split('?')[0];
  return network.request({
    url: API.SEND_SMSCODE,
    method: 'POST',
    data: {
      pId: packageId,
      channelCode,
      mobile,
      subStatTag,
      statisticParams,
      inviterToken: shareToken,
      promoterOrderId,
      pageUrl,
    },
    isGateway: true,
    headers: {
      ...getMobileTokenHeader(),
    },
  });
}

export function fetchSMSOrderGoods({ packageId }) {
  return network.request({
    url: API.WUJI_ORDER_GOODS_CONFIG,
    method: 'POST',
    data: {
      pID: packageId,
    },
    WUJI: true,
  });
}

export function fetchAMSRoleList({
  gameCode,
  amsChannelId,
  amsSystemId,
  amsPartitionId,
}: QueryAMSRoleData & BaseRequestParamsType) {
  return network.request({
    baseURL: urlConfig.amsUrl,
    url: API.QUERY_ROLE,
    method: 'POST',
    data: {
      game: gameCode,
      area: amsChannelId,
      platId: amsSystemId,
      partition: amsPartitionId,
    },
    isAMS: true,
  });
}

export function fetchMSDKDecrypt() {
  return network.request({
    url: API.MDSK_DECRYPT,
    method: 'POST',
    isAMS: true,
  });
}

export function fetchReceiveGift(params: ReceiveGiftRequestData) {
  const requestData = {
    actId: params.actId,
    platId: params.amsSystemId,
    area: params.amsChannelId,
    partition: params.amsPartitionId,
    roleId: params.roleId,
    roleName: params.roleName,
    channel: channelService.gameCode,
    qqUin: params.qqUin,
    userInfo: params.userInfo,
  };
  const qimeiParams = qimeiWebDefend.generateQimeiParams({
    ...requestData,
  });
  return network.request({
    url: API.AMS_DELIVER,
    method: 'POST',
    data: {
      ...requestData,
      ...qimeiParams,
    },
  });
}

export function fetchUserPrizeStatus({ actId }: GiftDataType) {
  return network.request({
    url: API.GET_USER_PRIZELIST,
    method: 'POST',
    data: {
      actId,
      offset: 0,
      pageSize: 20,
    },
    needLogin: true,
  });
}

export function fetchPrizeDetail({ actId }: GiftDataType) {
  return network.request({
    url: API.GET_ALL_PRIZES,
    method: 'POST',
    data: {
      actId,
    },
  });
}

// 调用抽奖接口
export function fetchLotteryPrize(params: GiftDataType) {
  const qimeiParams = qimeiWebDefend.generateQimeiParams({
    ...params,
    channel: channelService.gameCode,
  });
  return network.request({
    url: API.AMS_DELIVER,
    method: 'POST',
    data: {
      ...params,
      channel: channelService.gameCode,
      ...qimeiParams,
    },
  });
}

// 调用连续抽奖接口
export function multipleLotteryRequest(params: GiftDataType) {
  const qimeiParams = qimeiWebDefend.generateQimeiParams({
    ...params,
    isBatchDraw: true,
  });
  return network.request({
    url: API.MULTIPLE_LOTTERY,
    method: 'POST',
    data: {
      ...params,
      isBatchDraw: true,
      ...qimeiParams,
    },
  });
}

// 连续抽奖结果
export function getMultipleLotteryResultRequest(params?: GiftDataType) {
  return network.request({
    url: API.LOTTERY_RESULT,
    method: 'POST',
    data: {
      ...params,
    },
  });
}

// 抽奖轮播20条记录
export function getLotteryTopRecordRequest() {
  return network.request({
    url: API.RANK_TOP_LOTTERY_RECORD,
    method: 'POST',
  });
}

// 连续抽奖可抽奖次数
export function getMultipleLotteryCountRequest(params: ReceiveGiftRequestData) {
  return network.request({
    url: API.MULTIPLE_LOTTERY_COUNT,
    method: 'POST',
    data: {
      actId: params.actId,
    },
  });
}

// 领取抽奖奖品
export function fetchReceiveLotteryPrize(params: ReceiveGiftRequestData) {
  const requestData = {
    channel: channelService.gameCode,
    actId: params.actId,
    platId: params.amsSystemId,
    area: params.amsChannelId,
    partition: params.amsPartitionId,
    roleId: params.roleId,
    roleName: params.roleName,
    lotteryId: params.lotteryId,
    obdToken: params.obdToken,
    qqUin: params.qqUin,
  };
  const qimeiParams = qimeiWebDefend.generateQimeiParams({
    ...requestData,
  });
  return network.request({
    url: API.LOTTERY_REQ,
    method: 'POST',
    data: {
      ...requestData,
      ...qimeiParams,
    },
  });
}

// 领取合成礼包奖品
export function fetchSynthesisLotteryPrize(params: ReceiveGiftRequestData) {
  const requestData = {
    channel: channelService.gameCode,
    platId: params.amsSystemId,
    area: params.amsChannelId,
    partition: params.amsPartitionId,
    roleId: params.roleId,
    roleName: params.roleName,
    lotteryId: params.lotteryId,
    obdToken: params.obdToken,
  };
  const qimeiParams = qimeiWebDefend.generateQimeiParams({
    ...requestData,
  });
  return network.request({
    url: API.LOTTERY_REQ,
    method: 'POST',
    data: {
      ...requestData,
      ...qimeiParams,
    },
  });
}

export function receiveCashRequest(params) {
  const requestData = {
    channel: channelService.gameCode,
    lotteryId: params.lotteryId,
    qqUin: params.qqUin,
    actId: params.actId,
  };
  const qimeiParams = qimeiWebDefend.generateQimeiParams({
    ...requestData,
  });
  return network.request({
    url: API.LOTTERY_REQ,
    method: 'POST',
    data: {
      ...requestData,
      ...qimeiParams,
    },
  });
}

export function receiveCdkey(params) {
  const requestData = {
    channel: channelService.gameCode,
    lotteryId: params.lotteryId,
    qqUin: params.qqUin,
    actId: params.actId,
  };

  const qimeiParams = qimeiWebDefend.generateQimeiParams({
    ...requestData,
  });
  return network.request({
    url: API.LOTTERY_REQ,
    method: 'POST',
    data: {
      ...requestData,
      ...qimeiParams,
    },
  });
}

export function fetchAsyncDeliveryRecord({ actId }: GiftDataType) {
  return network.request({
    url: API.ASYNC_DELIVER_RECORD,
    method: 'POST',
    data: {
      actId,
    },
  });
}

export function saveAsyncDeliverRecord(params: ReceiveGiftRequestData) {
  return network.request({
    url: API.ASYNC_DELIVER_SAVE,
    method: 'POST',
    data: {
      actIds: params.actIds,
      platId: params.amsSystemId,
      channel: channelService.gameCode,
      roleId: params.roleId,
      partition: params.amsPartitionId,
      roleName: params.roleName,
      area: params.amsChannelId,
      partitionName: params.amsPartitionName,
    },
  });
}

export function queryActiveMobileCaptchaCode(mobile: string) {
  return network.request({
    url: API.APPLY_ACTIVE_SMSCODE,
    method: 'POST',
    data: {
      mobile,
    },
    isGateway: true,
    headers: {
      ...getMobileTokenHeader(),
    },
  });
}

export function fetchActiveMobile({ mobile, requestId, smsCode }: OrderRequestData) {
  return network.request({
    url: API.ACTIVE,
    method: 'POST',
    data: {
      mobile,
      requestId,
      smsCode,
    },
    isGateway: true,
    headers: {
      ...getMobileTokenHeader(),
    },
  });
}

export function fetchWUJIActivity() {
  return network.request({
    url: API.WUJI_ACTIVITY_CONFIG,
    method: 'POST',
    WUJI: true,
  });
}

export function fetchWUJISupplier() {
  return network.request({
    url: API.WUJI_SUPPLIER_CONFIG,
    method: 'POST',
    WUJI: true,
  });
}

export function fetchWUJISubscribePage() {
  return network.request({
    url: API.WUJI_PAGE_CONFIG,
    method: 'POST',
    WUJI: true,
  });
}

export function fetchShareToken() {
  return network.request({
    url: API.SHARE_TOKEN,
    method: 'POST',
    needLogin: true,
  });
}

// 获取用户游戏币数量
export function getUserCurrencyAmount() {
  return network.request({
    url: API.GAME_CURRENCY_USER,
    method: 'POST',
    needLogin: true,
  });
}

export function getGameCurrencyConfig() {
  return network.request({
    url: API.GAME_CURRENCY_CONFIG,
    method: 'POST',
    WUJI: true,
  });
}

export function checkExchangeQualifications({ productID }) {
  return network.request({
    url: API.GAME_CURRENCY_EXCHANGE_CHECK,
    method: 'POST',
    data: {
      productID,
    },
  });
}

export function submitExchangeCurrency({ productID, roleId, partitionName }) {
  return network.request({
    url: API.GAME_CURRENCY_EXCHANGE,
    method: 'POST',
    data: {
      productID,
      roleID: roleId,
      partitionName,
      channel: '定向',
    },
  });
}

export function queryAmsRoleForCurrency(data) {
  return network.request({
    url: API.GAME_CURRENCY_ROLE_LIST,
    method: 'POST',
    data: {
      partition: data.amsPartitionId,
      area: data.amsChannelId,
      platID: data.amsSystemId,
    },
  });
}

export function getPrizeConfigByActId({ actId, serviceType = '' }) {
  return network.request({
    url: API.WUJI_GET_ALL_PRIZES,
    method: 'POST',
    data: {
      actId,
      serviceType,
    },
    WUJI: true,
  });
}

export const saveDeliverUserInfo = (params) =>
  network.request({
    url: API.SAVE_DELIVER_USER_INFO,
    method: 'POST',
    data: params,
    needLogin: true,
  });

// 免费抽奖
export const freeLottery = ({ actId }) => {
  const qimeiParams = qimeiWebDefend.generateQimeiParams({
    actId,
    isBatchDraw: false,
  });
  return network.request({
    url: API.FREE_LOTTERY,
    method: 'POST',
    data: {
      actId,
      isBatchDraw: false,
      ...qimeiParams,
    },
  });
};

export const lottery = ({ actId }) => {
  const qimeiParams = qimeiWebDefend.generateQimeiParams({
    actId,
    isBatchDraw: false,
  });
  return network.request({
    url: API.FREE_LOTTERY,
    method: 'POST',
    data: {
      actId,
      isBatchDraw: false,
      ...qimeiParams,
    },
  });
};

// 免费抽奖次数获取
export const queryLotteryChance = ({ actId }) =>
  network.request({
    url: API.FREE_LOTTERY_CHANCE,
    method: 'POST',
    data: {
      actId,
    },
  });

export const fetchFreeGiftSmsCode = (mobile) =>
  network.request({
    url: API.FREE_GIFT_SMS_CODE,
    method: 'POST',
    data: {
      mobile,
    },
  });

export const receiveFreeGift = ({ mobile, smsCode, requestId }) =>
  network.request({
    url: API.RECEIVE_FREE_GIFT,
    method: 'POST',
    data: {
      mobile,
      requestId,
      smsCode,
    },
  });

export const receiveFreeGiftWithoutValid = ({ mobile }) =>
  network.request({
    url: API.RECEIVE_FREE_GIFT_WIHTOUT_VALID,
    method: 'POST',
    data: {
      mobile,
    },
    isGateway: true,
  });

export const signInRequest = (actId) =>
  network.request({
    url: API.SIGN_IN,
    method: 'POST',
    data: {
      actId: Number(actId),
    },
    isGateway: true,
  });

export const querySubscribeFailReason = (orderId) =>
  network.request({
    url: API.QUERY_SUBSCRIBE_FAIL_REASON,
    method: 'POST',
    data: {
      order_id: orderId,
    },
    isGateway: true,
  });

export const getRedeemCode = (redeemToken) =>
  network.request({
    url: API.GET_REDEEMCODE,
    method: 'POST',
    data: {
      redeemToken,
    },
    isGateway: true,
  });

export const getTopOrderRecordRequest = () =>
  network.request({
    url: API.TOP_ORDER_RECORD,
    method: 'POST',
    data: {},
    isGateway: true,
  });

export const getPrizePoolStatus = (params) =>
  network.request({
    url: API.GET_PRIZE_POOL_STATUS,
    method: 'POST',
    data: {
      ...params,
    },
    isGateway: true,
  });

export const getPrizePoolStatusV2 = (params) =>
  network.request({
    url: API.GET_PRIZE_POOL_STATUS_V2,
    method: 'POST',
    data: {
      ...params,
    },
    isGateway: true,
  });

export const getLatestAuthInfo = () =>
  network.request({
    url: API.GET_LATEST_AUTH_INFO,
    method: 'POST',
    data: {},
    isGateway: true,
  });
